import { atom } from "jotai";
import { Node, Edge } from "@xyflow/react";
import {
  chatbotsAppAtom,
  agentsAppAtom,
  interfacesAppAtom,
  MARKER,
  tablesAppAtom,
  isAgentsEnabledAtom,
} from "@zapier/canvas-state";
import { FALLBACK_IMPLEMENTATION_IDS } from "@zapier/canvas-constants";

type StreamedData = {
  key: string;
  name: string;
  description: string;
  parameters: Array<{ key: string; default: string }>;
  interfacePrompts?: {
    prompt: string;
    pages: Array<{
      name: string;
      description: string;
      blocks: Array<any>;
      connectedAssets: Array<string>;
    }>;
  };
  tablesPrompts?: {
    prompt: string;
    tables: Array<{
      id: string;
      name: string;
      purpose: string;
      fields: Array<any>;
      connectedAssets: Array<string>;
    }>;
  };
  zapPrompts?: Array<{
    zapId: string;
    prompt: string;
    apps: string[];
    zapStepDescriptions: string[];
    connectedAssets: Array<any>;
  }>;
  chatbotPrompts?: {
    prompt: string;
    chatbots: Array<{
      id: string;
      name: string;
      purpose: string;
      connectedAssets: Array<string>;
    }>;
  };
  agentPrompts?: {
    prompt: string;
    agents: Array<{
      id: string;
      name: string;
      purpose: string;
      connectedAssets: Array<string>;
    }>;
  };
};

export const zslOutlineToNodesAndEdgesAtom = atom((get) => {
  // Use the latest app implementation IDs
  const tablesApp = get(tablesAppAtom);
  const interfacesApp = get(interfacesAppAtom);
  const chatbotsApp = get(chatbotsAppAtom);
  const agentsApp = get(agentsAppAtom);
  const isAgentsEnabled = get(isAgentsEnabledAtom);
  // Return a pure converter that callers can invoke with streamed data.
  return (data: StreamedData): { nodes: Node[]; edges: Edge[] } => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const xSpacing = 400;
    const ySpacing = 380;

    // Initialize column positions with 60px padding
    const interfaceColumn = 60;
    const tableColumn = xSpacing + 60;
    const zapColumn = xSpacing * 2 + 60;
    const chatbotColumn = xSpacing * 3 + 60;

    // Track vertical positions for each column with 80px padding
    const columnPositions = {
      interface: 100,
      table: 100,
      zap: 100,
      chatbot: 100,
    };

    // Keep track of node IDs by asset key
    const nodeMap = new Map<string, string>();

    // Sequential node ID counter
    let nodeId = 1;

    // Add the addNode function
    const addNode = (
      label: string,
      key: string,
      type: "interface" | "table" | "zap" | "chatbot" | "agent",
      index: number,
    ) => {
      const id = `node-${nodeId++}`;
      nodeMap.set(key, id);

      let x = interfaceColumn;
      let y = columnPositions.interface;

      if (type === "table") {
        x = tableColumn;
        y = columnPositions.table;
        columnPositions.table += ySpacing;
      } else if (type === "zap") {
        x = zapColumn;
        y = columnPositions.zap;
        columnPositions.zap += ySpacing;
      } else if (type === "chatbot") {
        x = chatbotColumn;
        y = columnPositions.chatbot;
        columnPositions.chatbot += ySpacing;
      } else if (type === "agent") {
        x = zapColumn;
        y = columnPositions.zap;
        columnPositions.zap += ySpacing;
      } else {
        columnPositions.interface += ySpacing;
      }

      const nodeData: {
        label: string;
        app?: string;
        zapId?: string;
        chatbotId?: string;
        agentId?: string;
        services?: Array<{ currentImplementation: { id: string } }>;
        tableId?: string;
        projectId?: string;
        pageId?: string;
        isExpanded?: boolean;
      } = { label: label };

      if (type === "table") {
        nodeData.tableId = `_TABLE_${index + 1}`;
        nodeData.app = "Zapier Tables";
        nodeData.services = [
          {
            currentImplementation: {
              id:
                tablesApp?.current_implementation_id ||
                FALLBACK_IMPLEMENTATION_IDS.TABLES,
            },
          },
        ];
      } else if (type === "zap") {
        nodeData.zapId = `_ZAP_${index + 1}`;
      } else if (type === "chatbot") {
        nodeData.chatbotId = `_CHATBOT_${index + 1}`;
        nodeData.app = "Zapier Chatbots";
        nodeData.services = [
          {
            currentImplementation: {
              id:
                chatbotsApp?.current_implementation_id ||
                FALLBACK_IMPLEMENTATION_IDS.CHATBOTS,
            },
          },
        ];
      } else if (type === "agent") {
        nodeData.agentId = `_AGENT_${index + 1}`;
        nodeData.app = "Zapier Agents";
        nodeData.services = [
          {
            currentImplementation: {
              id:
                agentsApp?.current_implementation_id ||
                FALLBACK_IMPLEMENTATION_IDS.AGENTS,
            },
          },
        ];
      } else if (type === "interface") {
        // We hardcode the projectId because our ZSL outline only ever has one interface project with multiple pages
        nodeData.projectId = `_INTERFACE_1`;
        nodeData.pageId = `_PAGE_${index + 1}`;
        nodeData.app = "Zapier Interfaces";
        nodeData.services = [
          {
            currentImplementation: {
              id:
                interfacesApp?.current_implementation_id ||
                FALLBACK_IMPLEMENTATION_IDS.INTERFACES,
            },
          },
        ];
      }

      nodes.push({
        id,
        position: { x, y },
        data: nodeData,
        type: type === "zap" ? "zap" : "default",
      });
    };

    // First pass: Create all nodes

    // Add Interface nodes
    if (data.interfacePrompts?.pages?.length) {
      data.interfacePrompts.pages?.forEach((page, index) => {
        addNode(page.name, page.name, "interface", index);
      });
    }

    // Add Table nodes
    if (data.tablesPrompts?.tables?.length) {
      data.tablesPrompts.tables?.forEach((table, index) => {
        addNode(table.name, table.id, "table", index);
      });
    }

    // Add Zap nodes
    if (data.zapPrompts?.length) {
      data.zapPrompts?.forEach((zap, index) => {
        addNode(zap.prompt, zap.zapId, "zap", index);
      });
    }

    // Add Chatbot nodes
    if (data.chatbotPrompts?.chatbots?.length) {
      data.chatbotPrompts.chatbots.forEach((chatbot, index) => {
        addNode(chatbot.name, chatbot.id, "chatbot", index);
      });
    }

    // Add Agent nodes (guarded by agents entitlement check)
    if (data.agentPrompts?.agents?.length && isAgentsEnabled) {
      data.agentPrompts.agents.forEach((agent, index) => {
        addNode(agent.name, agent.id, "agent", index);
      });
    }

    // Second pass: Create all edges now that nodeMap is complete

    // Add Interface connections
    if (data.interfacePrompts?.pages?.length) {
      data.interfacePrompts.pages?.forEach((page) => {
        const sourceNodeId = nodeMap.get(page.name);
        if (!sourceNodeId) return;

        page.connectedAssets?.forEach((connectedAsset) => {
          const targetNodeId = nodeMap.get(connectedAsset);
          if (!targetNodeId) return;

          edges.push({
            id: `${sourceNodeId}=>${targetNodeId}`,
            source: sourceNodeId,
            target: targetNodeId,
            sourceHandle: "right",
            targetHandle: "left",
            markerEnd: MARKER,
          });
        });
      });
    }

    // Add Zap connections
    if (data.zapPrompts?.length) {
      data.zapPrompts?.forEach((zap) => {
        const targetNodeId = nodeMap.get(zap.zapId);
        if (!targetNodeId) return;

        zap.connectedAssets?.forEach((asset) => {
          const sourceNodeId = nodeMap.get(asset);
          if (!sourceNodeId) return;

          edges.push({
            id: `${sourceNodeId}=>${targetNodeId}`,
            source: sourceNodeId,
            target: targetNodeId,
            sourceHandle: "right",
            targetHandle: "left",
            markerEnd: MARKER,
          });
        });
      });
    }

    // Add Chatbot connections
    if (data.chatbotPrompts?.chatbots?.length) {
      data.chatbotPrompts.chatbots.forEach((chatbot) => {
        const targetNodeId = nodeMap.get(chatbot.id);
        if (!targetNodeId) return;

        chatbot.connectedAssets?.forEach((asset) => {
          const sourceNodeId = nodeMap.get(asset);
          if (!sourceNodeId) return;

          edges.push({
            id: `${sourceNodeId}=>${targetNodeId}`,
            source: sourceNodeId,
            target: targetNodeId,
            sourceHandle: "right",
            targetHandle: "left",
            markerEnd: MARKER,
          });
        });
      });
    }

    // Add Agent connections (guarded by agents entitlement check)
    if (data.agentPrompts?.agents?.length && isAgentsEnabled) {
      data.agentPrompts.agents.forEach((agent) => {
        const targetNodeId = nodeMap.get(agent.id);
        if (!targetNodeId) return;

        agent.connectedAssets?.forEach((asset) => {
          const sourceNodeId = nodeMap.get(asset);
          if (!sourceNodeId) return;

          edges.push({
            id: `${sourceNodeId}=>${targetNodeId}`,
            source: sourceNodeId,
            target: targetNodeId,
            sourceHandle: "right",
            targetHandle: "left",
            markerEnd: MARKER,
          });
        });
      });
    }

    return { nodes, edges };
  };
});

zslOutlineToNodesAndEdgesAtom.debugLabel = "zslOutlineToNodesAndEdgesAtom";
